import React from 'react';
import { ToastContainer, ToastContainerProps, toast } from 'react-toastify';
import { useTranslations } from 'next-intl';
import styles from './GlobalToast.module.css';
import successfulCheckMark from '@Assets/icons/successful_check_mark.svg';
import errorNotificationMark from '@Assets/icons/error_notification_mark.svg';

const AUTO_CLOSE_GLOBAL_TOAST_DURATION = 5000;

const ToastMessage = ({
    icon,
    message,
    additionalMessage
}: {
    icon: string;
    message: string;
    additionalMessage?: React.ReactNode;
}) => (
    <div className={styles.notificationBox}>
        <div className={styles.imageContainer}>
            <img src={icon} alt="icon" />
        </div>
        <div className={styles.textContainer}>
            <div className={styles.mainText}>{message}</div>
            {additionalMessage && (
                <div className={styles.additionalText}>{additionalMessage}</div>
            )}
        </div>
    </div>
);

/**
 * Component that translates toast messages using next-intl
 * This allows middleware to pass translation keys instead of hardcoded strings
 */
const TranslatedToastMessage = ({
    icon,
    messageKey,
    additionalMessage
}: {
    icon: string;
    messageKey: string;
    additionalMessage?: React.ReactNode;
}) => {
    const t = useTranslations();

    // Try to translate the message key, fallback to the key itself if translation doesn't exist
    const translatedMessage = t(messageKey as any) || messageKey;

    return (
        <ToastMessage
            icon={icon}
            message={translatedMessage}
            additionalMessage={additionalMessage}
        />
    );
};

export const setGlobalSuccessToastMessage = (
    message: string,
    additionalMessage?: React.ReactNode,
    autoClose = AUTO_CLOSE_GLOBAL_TOAST_DURATION,
    onClick?: (e: React.MouseEvent) => void

) => {
    toast.success(
        <TranslatedToastMessage
            icon={successfulCheckMark.src}
            messageKey={message}
            additionalMessage={additionalMessage}
        />,
        {
            className: `${styles.toast} ${styles.toastSuccess}`,
            autoClose,
            icon: false,
            onClick
        }
    );
};

export const setGlobalErrorToastMessage = (
    message: string,
    additionalMessage?: React.ReactNode
) => {
    toast.error(
        <TranslatedToastMessage
            icon={errorNotificationMark.src}
            messageKey={message}
            additionalMessage={additionalMessage}
        />,
        {
            className: `${styles.toast} ${styles.toastError}`,
            icon: false
        }
    );
};

const defaultGlobalToast: ToastContainerProps = {
    position: 'top-right',
    autoClose: AUTO_CLOSE_GLOBAL_TOAST_DURATION,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    theme: 'light',
    newestOnTop: false,
    rtl: false,
    pauseOnFocusLoss: true,
    closeButton: false
};

const GlobalToast = () => {
    return (
        <ToastContainer
            {...defaultGlobalToast}
            className={styles.toastContainer}
        />
    );
};

export default GlobalToast;
